// src/pages/OrderSuccess.js
import { useLocation, Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

export default function OrderSuccess() {
  const query = new URLSearchParams(useLocation().search);
  const orderId = query.get('order_id');
  const total = query.get('total');
  const paymentId = query.get('payment_id');
  const serviceType = query.get('service_type') || 'order';

  const getServiceDetails = () => {
    switch (serviceType) {
      case 'appointment':
        return {
          title: 'Appointment Booked Successfully!',
          description: 'Your medical appointment has been confirmed and payment processed.',
          icon: '🩺',
          features: [
            'SMS confirmation sent',
            'Calendar reminder set',
            'Payment receipt emailed'
          ]
        };
      case 'paramedical':
        return {
          title: 'Paramedical Service Booked!',
          description: 'Your home healthcare service has been scheduled and payment confirmed.',
          icon: '🏥',
          features: [
            'Service team assigned',
            'Home visit scheduled', 
            'Payment confirmation sent'
          ]
        };
      case 'medical-test':
        return {
          title: 'Medical Tests Booked Successfully!',
          description: 'Your lab tests have been scheduled for home collection.',
          icon: '🧪',
          features: [
            'Home collection scheduled',
            'Sample collection kit prepared',
            'Payment processed successfully'
          ]
        };
      case 'video-consultation':
        return {
          title: 'Video Consultation Booked!',
          description: 'Your video consultation with medical specialist is confirmed.',
          icon: '📹',
          features: [
            'Video link will be sent via WhatsApp',
            'Doctor assigned',
            'Payment confirmation sent'
          ]
        };
      default:
        return {
          title: 'Order Placed Successfully!',
          description: 'Your medicine order has been confirmed and payment processed.',
          icon: '📦',
          features: [
            'Order processing started',
            'Delivery tracking available',
            'Payment receipt generated'
          ]
        };
    }
  };

  const serviceDetails = getServiceDetails();

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-12 text-white text-center">
              <div className="text-6xl mb-6">{serviceDetails.icon}</div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {serviceDetails.title}
              </h1>
              <p className="text-green-100 text-lg max-w-md mx-auto">
                {serviceDetails.description}
              </p>
            </div>

            {/* Order Details */}
            <div className="px-8 py-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: '#10495e' }}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Order Details</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-semibold text-gray-900">{orderId}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="font-bold text-xl" style={{ color: '#10495e' }}>₹{total}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium text-gray-900">{new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {paymentId && (
                  <div className="bg-green-50 rounded-xl p-6">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Payment Confirmed</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Payment ID:</span>
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{paymentId}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-semibold text-green-600">Verified ✓</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Method:</span>
                        <span className="font-medium text-gray-900">Razorpay</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What happens next?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {serviceDetails.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm text-blue-800 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Important Information */}
              <div className="border border-blue-200 rounded-xl p-6 mb-8" style={{ backgroundColor: '#f0f9ff' }}>
                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-blue-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Important Information</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>• You will receive SMS and email confirmations shortly</p>
                      <p>• Payment receipt has been sent to your registered email</p>
                      <p>• For any queries, contact our support team with your Order ID</p>
                      {serviceType === 'appointment' && <p>• Please arrive 10 minutes before your appointment time</p>}
                      {serviceType === 'video-consultation' && <p>• Video call link will be sent 15 minutes before appointment</p>}
                      {serviceType === 'medical-test' && <p>• Ensure someone is available at home during collection time</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/" 
                  className="flex-1 text-center px-6 py-3 text-white font-semibold rounded-xl transition-all duration-200 hover:opacity-90 hover:scale-105 shadow-lg"
                  style={{ backgroundColor: '#10495e' }}
                >
                  🏠 Back to Home
                </Link>
                <Link 
                  to="/profile/orders" 
                  className="flex-1 text-center px-6 py-3 border-2 font-semibold rounded-xl transition-all duration-200 hover:bg-gray-50"
                  style={{ borderColor: '#10495e', color: '#10495e' }}
                >
                  📊 View My Orders
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
